/**
 * Cash Management React Hooks
 * Story 62-12: Cash Management Frontend UI
 *
 * TanStack Query hooks for cash register, blind close, SZH management.
 */

import {
  cancelCredit,
  cancelExpenseVoucher,
  finalizeClose,
  generateSzhReport,
  getCloseSummary,
  getDailySummary,
  getReserveBalance,
  getSzhSummary,
  getTodayRegister,
  issueAnonymousCredit,
  issueCredit,
  issueExpenseVoucher,
  listExpenseVouchers,
  listSzhItems,
  openCashRegister,
  recordTransaction,
  settleCredit,
  settleExpenseVoucher,
  startBlindClose,
  submitCount,
  type CashRegister,
  type CloseSummaryResult,
  type DailySummary,
  type ExpenseVoucher,
  type ReserveBalance,
  type SupplierCredit,
  type SzhSummary,
} from '@/api/cash-management';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ============================================
// CASH REGISTER
// ============================================

export function useTodayRegister() {
  const query = useQuery({
    queryKey: ['cash', 'today'],
    queryFn: async () => {
      const res = await getTodayRegister();
      return res.data;
    },
    retry: false,
  });

  return {
    register: query.data as CashRegister | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useCashSummary(registerId: string | undefined) {
  const query = useQuery({
    queryKey: ['cash', 'summary', registerId],
    queryFn: async () => {
      if (!registerId) throw new Error('No register');
      const res = await getDailySummary(registerId);
      return res.data;
    },
    enabled: !!registerId,
  });

  return {
    summary: query.data as DailySummary | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useOpenRegister() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (carryOver?: boolean) => openCashRegister(carryOver),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash'] });
    },
  });
}

export function useRecordTransaction(registerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      pocketType: 'REGISTER' | 'DEPOSIT' | 'CREDIT';
      amount: number;
      direction: 'IN' | 'OUT';
      category?: string;
      note?: string;
      expenseCategory?: string;
    }) => recordTransaction(registerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash', 'summary', registerId] });
    },
  });
}

// ============================================
// DAILY CLOSE (NAPI ZÁRÁS) — P-CSH-1: nyílt zárás
// ============================================

/** P-CSH-1: Fetch expected amounts for nyílt zárás (boltvezető+ only) */
export function useCloseSummary(registerId: string | undefined) {
  const query = useQuery({
    queryKey: ['cash', 'close-summary', registerId],
    queryFn: async () => {
      if (!registerId) throw new Error('No register');
      const res = await getCloseSummary(registerId);
      return res.data;
    },
    enabled: !!registerId,
  });

  return {
    closeSummary: query.data as CloseSummaryResult | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
  };
}

export function useBlindClose(registerId: string) {
  const queryClient = useQueryClient();

  const start = useMutation({
    mutationFn: () => startBlindClose(registerId),
  });

  const submit = useMutation({
    mutationFn: (pocketCounts: Array<{ pocketType: string; physicalAmount: number }>) =>
      submitCount(registerId, pocketCounts),
  });

  const finalize = useMutation({
    mutationFn: (pocketCounts: Array<{ pocketType: string; physicalAmount: number }>) =>
      finalizeClose(registerId, pocketCounts),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash'] });
    },
  });

  return { start, submit, finalize };
}

// ============================================
// SZH (SUPPLIER CREDIT)
// ============================================

export function useSzhItems(registerId: string | undefined, status?: string) {
  const query = useQuery({
    queryKey: ['cash', 'szh', registerId, status],
    queryFn: async () => {
      if (!registerId) throw new Error('No register');
      const res = await listSzhItems(registerId, status);
      return res.data;
    },
    enabled: !!registerId,
  });

  return {
    items: (query.data ?? []) as SupplierCredit[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useSzhSummary(registerId: string | undefined) {
  const query = useQuery({
    queryKey: ['cash', 'szh', 'summary', registerId],
    queryFn: async () => {
      if (!registerId) throw new Error('No register');
      const res = await getSzhSummary(registerId);
      return res.data;
    },
    enabled: !!registerId,
  });

  return {
    summary: query.data as SzhSummary | undefined,
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

export function useIssueCredit(registerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      partnerId?: string;
      partnerName?: string;
      amount: number;
      activity: string;
    }) => issueCredit(registerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash', 'szh'] });
    },
  });
}

export function useIssueAnonymousCredit(registerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { amount: number; activity: string }) =>
      issueAnonymousCredit(registerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash', 'szh'] });
    },
  });
}

export function useSettleCredit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (creditId: string) => settleCredit(creditId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash', 'szh'] });
    },
  });
}

export function useCancelCredit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (creditId: string) => cancelCredit(creditId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash', 'szh'] });
    },
  });
}

export function useReserveBalance(registerId: string | undefined) {
  const query = useQuery({
    queryKey: ['cash', 'szh', 'reserve', registerId],
    queryFn: async () => {
      if (!registerId) throw new Error('No register');
      const res = await getReserveBalance(registerId);
      return res.data;
    },
    enabled: !!registerId,
  });

  return {
    balance: query.data as ReserveBalance | undefined,
    isLoading: query.isLoading,
  };
}

export function useSzhReport(registerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notes?: string) => generateSzhReport(registerId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash', 'szh'] });
    },
  });
}

// ============================================
// EXPENSE VOUCHERS (Story 62-8, 62-9)
// ============================================

export function useExpenseVouchers(status?: string) {
  const query = useQuery({
    queryKey: ['cash', 'expense-vouchers', status],
    queryFn: async () => {
      const res = await listExpenseVouchers(status);
      return res.data;
    },
  });

  return {
    vouchers: (query.data ?? []) as ExpenseVoucher[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useIssueExpenseVoucher() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      recipientId: string;
      recipientName: string;
      amount: number;
      purpose: string;
    }) => issueExpenseVoucher(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash', 'expense-vouchers'] });
    },
  });
}

export function useSettleExpenseVoucher() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      settledAmount: number;
      returnedAmount?: number;
      receiptAttached?: boolean;
    }) => settleExpenseVoucher(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash', 'expense-vouchers'] });
    },
  });
}

export function useCancelExpenseVoucher() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelExpenseVoucher(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cash', 'expense-vouchers'] });
    },
  });
}
